import { Dialog } from "@/components/ui/Dialog";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";
import { relPast } from "@/lib/format";
import { formatMemoAudit } from "@/lib/memoAudit";
import { useMemoAudit } from "@/hooks/useMemo";
import type { MemoAuditEntry, UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  memoId: UUID;
}

function Diff({ from, to }: { from?: string; to?: string }) {
  if (from === undefined && to === undefined) return null;
  return (
    <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 3, lineHeight: 1.45 }}>
      {from !== undefined ? (
        <span style={{ textDecoration: "line-through", color: "var(--ink-4)" }}>{from}</span>
      ) : null}
      {from !== undefined && to !== undefined ? (
        <span style={{ color: "var(--ink-4)" }}> → </span>
      ) : null}
      {to !== undefined ? <span style={{ color: "var(--ink-1)" }}>{to}</span> : null}
    </div>
  );
}

function Row({ entry }: { entry: MemoAuditEntry }) {
  const line = formatMemoAudit(entry);
  const actor = entry.actor_name ?? "Jemand";
  return (
    <div style={{ display: "flex", gap: 10, padding: "9px 2px" }}>
      <span
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: 999,
          background: "var(--surface-soft)",
          border: "1px solid var(--hairline)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 1,
        }}
      >
        <Icon name={line.icon} size={13} color="var(--ink-3)" />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--ink-1)", lineHeight: 1.4 }}>
          <span style={{ fontWeight: 600 }}>{actor}</span> {line.phrase}
          {line.label ? <span style={{ color: "var(--ink-2)" }}> „{line.label}“</span> : null}
        </div>
        <Diff from={line.from} to={line.to} />
      </div>
      <span
        style={{ flexShrink: 0, fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}
        title={new Date(entry.created_at).toLocaleString("de-DE")}
      >
        {relPast(entry.created_at)}
      </span>
    </div>
  );
}

export function MemoAuditDrawer({ open, onOpenChange, memoId }: Props) {
  const { data: entries = [], isLoading } = useMemoAudit(memoId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t.memoAuditTitle} width={560}>
      <div
        style={{
          marginTop: 4,
          display: "flex",
          flexDirection: "column",
          maxHeight: 460,
          overflowY: "auto",
        }}
      >
        {isLoading ? <div style={{ color: "var(--ink-3)", fontSize: 13 }}>…</div> : null}
        {!isLoading && entries.length === 0 ? (
          <div style={{ color: "var(--ink-4)", fontSize: 13, padding: "8px 2px" }}>
            {t.memoAuditEmpty}
          </div>
        ) : null}
        {entries.map((a, i) => (
          <div
            key={a.id}
            style={{ borderTop: i === 0 ? undefined : "1px solid var(--hairline-soft)" }}
          >
            <Row entry={a} />
          </div>
        ))}
      </div>
    </Dialog>
  );
}
